import React from 'react';
import styled from 'astroturf/react';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import routes from 'js/core_pre_webpack/routes';

interface PodcastData {
  id: number;
  workId: number;
  workTitle: string;
  workAuthors: string;
  workThumbnailUrl: string | null;
  score?: number;
  source: string;
}

interface Props {
  podcasts: PodcastData[];
}

export const RecommendedPodcasts: React.FC<Props> = ({ podcasts }) => {
  // Return null if no podcasts
  if (!podcasts || podcasts.length === 0) {
    return null;
  }

  const extra = {
    rec_type: podcasts[0]?.source,
  };

  return (
    <ImpressionWatcher
      active={true}
      source="podcast_lib.home.sidebar"
      extra={extra}
    >
      <Container>
        <Header>Recommended podcasts</Header>
        <PodcastList>
          {podcasts.map((podcast) => (
            <PodcastItem key={podcast.id}>
              <Title>{podcast.workTitle}</Title>
              <ListenLink
                href={`${routes.podcast_library_url()}?recommendedPodcastId=${podcast.id}`}
                aria-label={`Listen to ${podcast.workTitle}`}
              >
                <LinkContent>
                  <Icon name="headphones" size={18} />
                  <LinkText>Listen now</LinkText>
                </LinkContent>
              </ListenLink>
            </PodcastItem>
          ))}
        </PodcastList>
      </Container>
    </ImpressionWatcher>
  );
};

// Styled components using Astroturf
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const Header = styled.h2`
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #141413;
  margin: 0;
`;

const PodcastList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const PodcastItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
`;

const Title = styled.p`
  font-family: 'DM Sans', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
  color: #141413;
  margin: 0;

  /* 3-line truncation */
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
`;

const ListenLink = styled.a`
  display: inline-block;
  text-decoration: none;
  cursor: pointer;
  color: #0645b1;

  &:focus-visible {
    outline: 2px solid #0645b1;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const LinkContent = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid transparent;

  ${ListenLink}:hover & {
    border-bottom-color: #0645b1;
  }
`;

const LinkText = styled.span`
  font-family: 'DM Sans', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: #0645b1;
`;
